import { useState } from "react";
import { dcLoan, dcFit, friendlyCalcError } from "@/lib/api";
import { fmtMoney, fmtPct, num } from "@/lib/format";
import { track } from "@/lib/analytics";
import { Field, CurrencyInput, PercentInput, TermSelect, CreditSelect } from "@/components/calc/fields";
import FitGauge from "@/components/FitGauge";

export const Tile = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-stone-50 border border-stone-200 p-4">
    <div className="text-xs font-medium text-stone-500">{label}</div>
    <div className="text-lg font-bold text-stone-900 mt-0.5" data-testid={testid} style={{ fontFeatureSettings: '"tnum" 1' }}>{value}</div>
  </div>
);

const PRIMARY_BTN =
  "w-full h-13 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold text-base transition-colors shadow-sm";

export default function HeroCalc({ onFitComplete }) {
  const [stage, setStage] = useState("deal"); // deal | payment | fit | fitResult
  const [deal, setDeal] = useState({ price: "45000", down: "5000", trade: "0", term: "72", creditScore: "700", useApr: false, apr: "7.5" });
  const [fin, setFin] = useState({ income: "", debts: "", housing: "" });
  const [loan, setLoan] = useState(null);
  const [fit, setFit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);

  const setD = (k) => (v) => {
    if (!started) { setStarted(true); track("calculator_started", { page: "home" }); }
    setDeal({ ...deal, [k]: v });
  };
  const setF = (k) => (v) => setFin({ ...fin, [k]: v });

  const dealPayload = () => ({
    vehicle_price: num(deal.price),
    down_payment: num(deal.down),
    trade_equity: num(deal.trade),
    term_months: parseInt(deal.term, 10),
    apr: deal.useApr ? num(deal.apr) : null,
    credit_score: parseInt(deal.creditScore, 10),
  });

  const calculate = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await dcLoan(dealPayload());
      setLoan(res.data);
      setStage("payment");
      track("calculator_completed", { page: "home" });
    } catch (err) { setError(friendlyCalcError(err)); }
    finally { setLoading(false); }
  };

  const seeFit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const payload = {
      ...dealPayload(),
      monthly_income: num(fin.income),
      monthly_debts: num(fin.debts),
      housing_cost: num(fin.housing),
      vehicle_age_years: 0,
    };
    try {
      const res = await dcFit(payload);
      setFit(res.data);
      setStage("fitResult");
      track("fit_completed", { page: "home" });
      onFitComplete?.(res.data, payload);
    } catch (err) { setError(friendlyCalcError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="hero-calculator" className="bg-white rounded-3xl border border-stone-200 shadow-xl shadow-stone-900/5 p-6 sm:p-8">
      {stage === "deal" && (
        <form onSubmit={calculate} data-testid="deal-form">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1">Payment Calculator</div>
          <h2 className="text-xl font-extrabold text-stone-900 mb-5">Run the numbers</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Vehicle price" htmlFor="deal-price">
              <CurrencyInput id="deal-price" value={deal.price} onChange={setD("price")} />
            </Field>
            <Field label="Down payment" htmlFor="deal-down">
              <CurrencyInput id="deal-down" value={deal.down} onChange={setD("down")} />
            </Field>
            <Field label="Trade-in equity" htmlFor="deal-trade" help="Negative if you owe more than it's worth">
              <CurrencyInput id="deal-trade" value={deal.trade} onChange={setD("trade")} />
            </Field>
            <Field label="Loan term" htmlFor="deal-term">
              <TermSelect id="deal-term" value={deal.term} onChange={setD("term")} />
            </Field>
            <div className="sm:col-span-2">
              {deal.useApr ? (
                <Field label="APR" htmlFor="deal-apr" help="Your expected interest rate">
                  <PercentInput id="deal-apr" value={deal.apr} onChange={setD("apr")} />
                </Field>
              ) : (
                <Field label="Credit score range" htmlFor="deal-credit" help="DriveClear estimates the APR for your tier">
                  <CreditSelect id="deal-credit" value={deal.creditScore} onChange={setD("creditScore")} />
                </Field>
              )}
              <button
                type="button"
                data-testid="deal-toggle-apr"
                onClick={() => setD("useApr")(!deal.useApr)}
                className="text-xs font-semibold text-blue-700 hover:text-blue-800 mt-2"
              >
                {deal.useApr ? "Use my credit score range instead" : "I know my APR — enter it directly"}
              </button>
            </div>
          </div>
          {error && <p data-testid="deal-error" className="text-sm text-rose-600 mt-3">{error}</p>}
          <div className="mt-5">
            <button type="submit" disabled={loading} data-testid="calculate-button" className={PRIMARY_BTN}>
              {loading ? "Calculating..." : "Calculate"}
            </button>
            <p className="text-xs text-stone-500 text-center mt-2.5">Free calculation • No account required</p>
          </div>
        </form>
      )}

      {stage === "payment" && loan && (
        <div data-testid="payment-result">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1">Estimated payment</div>
          <div className="text-5xl font-extrabold text-stone-900 mb-6" data-testid="result-payment" style={{ fontFeatureSettings: '"tnum" 1' }}>
            {fmtMoney(loan.monthly_payment)}<span className="text-lg font-semibold text-stone-500">/mo</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <Tile label="Amount financed" value={fmtMoney(loan.amount_financed)} testid="result-financed" />
            <Tile label="Total interest" value={fmtMoney(loan.total_interest)} testid="result-interest" />
            <Tile label="Estimated total cost" value={fmtMoney(loan.total_cost)} testid="result-total-cost" />
          </div>
          <div className="rounded-2xl bg-blue-50 border border-blue-100 p-5 mb-5">
            <p className="text-sm text-stone-700 leading-relaxed">
              A payment tells you what the loan costs. <strong className="text-stone-900">But does this car actually fit your finances?</strong>
            </p>
          </div>
          {error && <p data-testid="fit-error" className="text-sm text-rose-600 mb-3">{error}</p>}
          <button
            type="button"
            data-testid="check-fit-button"
            onClick={() => { setStage("fit"); track("fit_started", { page: "home" }); }}
            className={PRIMARY_BTN}
          >
            Check My DriveClear Fit &rarr;
          </button>
          <button type="button" data-testid="adjust-deal-button" onClick={() => setStage("deal")} className="w-full text-sm font-semibold text-stone-500 hover:text-stone-800 mt-3">
            &larr; Adjust the numbers
          </button>
        </div>
      )}

      {stage === "fit" && (
        <form onSubmit={seeFit} data-testid="fit-form">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1">DriveClear Fit Check</div>
          <h2 className="text-xl font-extrabold text-stone-900 mb-1">Does it fit your finances?</h2>
          <p className="text-sm text-stone-500 mb-5">Three more numbers. Never stored, never shared.</p>
          <div className="grid grid-cols-1 gap-4">
            <Field label="Monthly income (before tax)" htmlFor="fit-income">
              <CurrencyInput id="fit-income" value={fin.income} onChange={setF("income")} placeholder="8,000" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Monthly debts" htmlFor="fit-debts" help="Cards, loans, etc.">
                <CurrencyInput id="fit-debts" value={fin.debts} onChange={setF("debts")} placeholder="600" />
              </Field>
              <Field label="Housing cost" htmlFor="fit-housing" help="Rent or mortgage">
                <CurrencyInput id="fit-housing" value={fin.housing} onChange={setF("housing")} placeholder="2,000" />
              </Field>
            </div>
          </div>
          {error && <p data-testid="fit-error" className="text-sm text-rose-600 mt-3">{error}</p>}
          <div className="mt-5">
            <button type="submit" disabled={loading} data-testid="see-fit-button" className={PRIMARY_BTN}>
              {loading ? "Checking..." : "See My Fit"}
            </button>
            <p className="text-xs text-stone-500 text-center mt-2.5">Educational estimate — not a credit offer or lending decision</p>
          </div>
        </form>
      )}

      {stage === "fitResult" && fit && (
        <div data-testid="fit-result" className="text-center">
          <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-4">Your DriveClear Fit</div>
          <FitGauge
            score={fit.fit_score}
            min={fit.gauge?.min ?? 5}
            max={fit.gauge?.max ?? 95}
            label={fit.fit_status?.label ?? ""}
            tone={fit.fit_status?.tone ?? "stretch"}
          />
          <div className="grid grid-cols-2 gap-3 mt-6 text-left">
            <Tile label="Payment" value={fmtMoney(fit.monthly_payment)} testid="fit-payment" />
            <Tile label="All-in monthly" value={fmtMoney(fit.all_in_monthly)} testid="fit-all-in" />
            <Tile label="DTI" value={fmtPct(fit.dti_pct)} testid="fit-dti" />
            <Tile label="LTV" value={fmtPct(fit.ltv_pct)} testid="fit-ltv" />
          </div>
          <a href="#beyond" data-testid="fit-continue-scroll" className={`${PRIMARY_BTN} inline-flex items-center justify-center mt-6 no-underline`}>
            See what DriveClear unlocks &darr;
          </a>
        </div>
      )}

      {stage === "fitResult" && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-stone-200 p-3" data-testid="sticky-fit-cta">
          <a href="#beyond" className="block w-full py-3.5 rounded-xl bg-blue-600 text-white text-center font-semibold text-base">
            Continue This Fit in DriveClear
          </a>
        </div>
      )}
    </div>
  );
}
